/**
 * WHAT COVERS THE LOBBY.
 *
 * A lobby surface (the Collection, the Foundry, the Store, the summoner
 * select, Feedback, Referrals) takes the screen by writing its name to
 * `document.body.dataset.lobbySurfaceOwner` and gives it back by removing it
 * (each surface's claimLobbySurface / releaseLobbySurface; the Store opened
 * over the Collection hands the name back to it).
 *
 * What the lobby warms ahead of a press is for the lobby. While a surface
 * covers it, the screen the player opened owns the link: on 1 Mbps the
 * summoner select's portrait warm (four portraits, 1.8 MB, two at a time)
 * started 0.4 s after the Collection opened and took its link for the whole
 * 30 s its page's pictures had (28 September 2026, the Collection's
 * slow-link spec). The owner puts the cold, far-away phone on a slow link
 * first. So a lobby warm waits while one is open (lobby.js whileLobbyIdle),
 * and the portrait warm yields one it has on the wire (deck-selector.js).
 *
 * One watcher serves every waiter and listener, and only while there is one.
 */

const ATTRIBUTE = 'data-lobby-surface-owner';

/** @typedef {{ except: string | null, resolve: () => void }} CoverWaiter */

const LobbyCover = (function () {
    /** @type {Set<CoverWaiter>} */
    const waiters = new Set();
    /** @type {Set<(owner: string | null) => void>} */
    const listeners = new Set();
    /** @type {MutationObserver | null} */
    let observer = null;

    /** The surface on top of the lobby, or null. */
    function owner() {
        try {
            return document.body?.dataset?.lobbySurfaceOwner || null;
        } catch (_) {
            return null;
        }
    }

    /**
     * Whether a surface other than `except` covers the lobby (the summoner
     * select's own warm is what its dialog waits for, so it passes itself).
     * @param {{ except?: string | null }} [options]
     */
    function isCovered({ except = null } = {}) {
        const current = owner();
        return Boolean(current) && current !== except;
    }

    function settle() {
        const current = owner();
        for (const waiter of [...waiters]) {
            if (current && current !== waiter.except) continue;
            waiters.delete(waiter);
            waiter.resolve();
        }
        listeners.forEach((listener) => {
            try {
                listener(current);
            } catch (error) {
                console.warn('[LobbyCover] A listener failed:', error);
            }
        });
        release();
    }

    function watch() {
        if (observer) return true;
        if (typeof MutationObserver !== 'function' || !document.body) return false;
        observer = new MutationObserver(settle);
        observer.observe(document.body, { attributes: true, attributeFilter: [ATTRIBUTE] });
        return true;
    }

    function release() {
        if (!observer || waiters.size > 0 || listeners.size > 0) return;
        observer.disconnect();
        observer = null;
    }

    /**
     * Resolves once no surface other than `except` covers the lobby: at once
     * when none does, else when it closes, however long that takes (it is the
     * player's own screen, not a hang). Without a watcher to be had, at once:
     * the warms go as they did before.
     * @param {{ except?: string | null }} [options]
     * @returns {Promise<void>}
     */
    function whenUncovered({ except = null } = {}) {
        if (!isCovered({ except })) return Promise.resolve();
        return new Promise((resolve) => {
            if (!watch()) {
                resolve();
                return;
            }
            waiters.add({ except, resolve });
        });
    }

    /**
     * Told the surface's name (or null) on every change, until the returned
     * function is called.
     * @param {(owner: string | null) => void} listener
     * @returns {() => void}
     */
    function onChange(listener) {
        if (typeof listener !== 'function' || !watch()) return () => {};
        listeners.add(listener);
        return () => {
            listeners.delete(listener);
            release();
        };
    }

    return { owner, isCovered, whenUncovered, onChange };
})();

export { LobbyCover };
